// ============================================================
// INSPECTION CALL SERVICE
// ============================================================
// Description: API service for managing inspection calls (RM, Process, Final)
// Handles CRUD operations and filtering based on approval status
// ============================================================

import httpClient from './httpClient';
// eslint-disable-next-line no-unused-vars
import { API_ENDPOINTS } from './apiConfig';
import { getStoredUser } from '../services/authService';


/**
 * Inspection Call Service
 * Provides methods for creating and fetching inspection calls
 */

const normalizeIc = (ic) => {
  if (!ic) return ic;

  const parts = ic.split('/');

  // E/ER-02040020/RAHIV → ER-02040020
  if (parts.length >= 2) {
    return parts[1];
  }

  return ic; // already ER-xxxx
};
const inspectionCallService = {


  // ============================================================
  // RAW MATERIAL INSPECTION CALLS
  // ============================================================

  /**
   * Create a new Raw Material Inspection Call
   * @param {Object} rmInspectionData - RM inspection call data
   * @returns {Promise<Object>} - API response with created IC details including auto-generated IC number
   */
  createRMInspectionCall: async (rmInspectionData) => {

    const user = getStoredUser();
    try {
      console.log('📥 Raw inspection data received:', rmInspectionData);

      // IC Number will be generated by backend - DO NOT send it from frontend

      // Transform frontend data structure to match backend DTO
      const transformedData = {
        inspectionCall: {
          // icNumber is NOT sent - backend will auto-generate it
          poNo: rmInspectionData.po_no,
          poSerialNo: rmInspectionData.po_serial_no,
          typeOfCall: rmInspectionData.type_of_call,
          ercType: rmInspectionData.type_of_erc || '',
          status: 'PENDING',
          desiredInspectionDate: rmInspectionData.desired_inspection_date,
          placeOfInspection: rmInspectionData.placeOfInspection, // POI code from form
          companyId: parseInt(rmInspectionData.company_id) || null,
          companyName: rmInspectionData.company_name,
          vendorId: user.userName,
          unitId: parseInt(rmInspectionData.unit_id) || null,
          unitName: rmInspectionData.unit_name,
          unitAddress: rmInspectionData.unit_address,
          remarks: rmInspectionData.remarks || '',
          createdBy: parseInt(user.userId) || user.userId, // Use logged-in userId
          updatedBy: parseInt(user.userId) || user.userId,  // Also set updatedBy
          callNature: rmInspectionData.call_nature || rmInspectionData.callNature || 'FRESH',
          isDivertedMaterial: rmInspectionData.call_nature === 'DIVERTED' || Boolean(rmInspectionData.is_diverted_material),
          sourceIcNo: rmInspectionData.source_ic_no || null
        },
        rmInspectionDetails: {
          // Required fields - use defaults if not provided (for testing without inventory)
          itemDescription: rmInspectionData.po_description || 'Test Item Description',
          itemQuantity: parseInt(rmInspectionData.po_qty) || 1,
          consigneeZonalRailway: rmInspectionData.rm_heat_tc_mapping?.[0]?.consigneeZonalRailway || 'N/A',
          heatNumbers: rmInspectionData.rm_heat_tc_mapping?.map(h => h.heatNumber).join(',') || 'TEST-HEAT-001',
          tcNumber: rmInspectionData.rm_heat_tc_mapping?.[0]?.tcNumber || 'TEST-TC-001',
          tcDate: rmInspectionData.rm_heat_tc_mapping?.[0]?.tcDate || rmInspectionData.desired_inspection_date,
          tcQuantity: parseFloat(rmInspectionData.rm_total_offered_qty_mt) || 1.0, // Must be Double
          manufacturer: rmInspectionData.rm_heat_tc_mapping?.[0]?.manufacturer || 'Test Manufacturer',
          supplierName: rmInspectionData.rm_heat_tc_mapping?.[0]?.manufacturer || 'Test Supplier',
          supplierAddress: rmInspectionData.rm_heat_tc_mapping?.[0]?.supplierAddress || '',
          invoiceNumber: rmInspectionData.rm_heat_tc_mapping?.[0]?.invoiceNo || '',
          invoiceDate: rmInspectionData.rm_heat_tc_mapping?.[0]?.invoiceDate || null,
          subPoNumber: rmInspectionData.rm_heat_tc_mapping?.[0]?.subPoNumber || '',
          subPoDate: rmInspectionData.rm_heat_tc_mapping?.[0]?.subPoDate || null,
          subPoQty: parseInt(rmInspectionData.rm_heat_tc_mapping?.[0]?.subPoQty) || null,
          totalOfferedQtyMt: parseFloat(rmInspectionData.rm_total_offered_qty_mt) || 1.0, // Must be Double
          offeredQtyErc: parseInt(rmInspectionData.rm_offered_qty_erc) || 1,
          unitOfMeasurement: rmInspectionData.po_unit || 'MT',

          // Heat Quantities - match backend DTO exactly
          heatQuantities: rmInspectionData.heatQuantities?.map(heat => ({
            heatNumber: heat.heatNumber,
            manufacturer: heat.manufacturer || 'Test Manufacturer',
            offeredQty: parseFloat(heat.offeredQty) || 0.0, // Backend expects 'offeredQty' not 'quantityMt'
            tcNumber: heat.tcNumber || '',
            tcDate: heat.tcDate || null,
            tcQuantity: parseFloat(heat.tcQuantity) || null,
            qtyLeft: null,
            qtyAccepted: null,
            qtyRejected: null,
            rejectionReason: null
          })) || [],

          // Chemical Analysis - per heat number (now supports multiple heats)
          chemicalAnalysis: rmInspectionData.chemicalAnalysis?.map(chem => ({
            heatNumber: chem.heatNumber,
            carbon: chem.carbon ? parseFloat(chem.carbon) : null,
            manganese: chem.manganese ? parseFloat(chem.manganese) : null,
            silicon: chem.silicon ? parseFloat(chem.silicon) : null,
            sulphur: chem.sulphur ? parseFloat(chem.sulphur) : null,
            phosphorus: chem.phosphorus ? parseFloat(chem.phosphorus) : null,
            chromium: chem.chromium ? parseFloat(chem.chromium) : null
          })) || []
        }
      };

      console.log('📤 Transformed data for backend:', JSON.stringify(transformedData, null, 2));

      const response = await httpClient.post(
        '/raw-material/inspectionCall',
        transformedData
      );

      console.log('✅ Backend response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error creating RM inspection call:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error data:', error.data);
      console.error('❌ Error response status:', error.data?.responseStatus);
      console.error('❌ Full error object:', JSON.stringify(error.data, null, 2));
      throw error;
    }
  },

  /**
   * Get all Raw Material Inspection Calls
   * @param {Object} filters - Optional filters (status, po_no, etc.)
   * @returns {Promise<Object>} - API response with list of RM ICs
   */
  getRMInspectionCalls: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams
        ? `/inspection-calls/raw-material?${queryParams}`
        : '/inspection-calls/raw-material';

      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching RM inspection calls:', error);
      throw error;
    }
  },

  /**
   * Get approved Raw Material Inspection Calls for a specific PO
   * Used in Process Inspection dropdown
   * @param {string} poNo - Purchase Order Number
   * @param {string} poSerialNo - PO Serial Number
   * @returns {Promise<Object>} - API response with approved RM ICs
   */
  getApprovedRMInspectionCalls: async (poNo, poSerialNo) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/raw-material/approved?po_no=${encodeURIComponent(poNo)}&po_serial_no=${encodeURIComponent(poSerialNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching approved RM inspection calls:', error);
      throw error;
    }
  },

  /**
   * Get heat details by PO Serial No
   * @param {string} poSrNo - PO Serial Number
   * @returns {Promise<Object>} - API response with heat details
   */
  getHeatDetailsByPoSrNo: async (poSrNo) => {
    try {
      // Need to prefix with /rm-inspection/ since that's what the backend endpoint uses
      const response = await httpClient.get(
        `/rm-inspection/heat-details/by-po-item?poSrNo=${encodeURIComponent(poSrNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching heat details by PO Item:', error);
      throw error;
    }
  },

  /**
   * Get RM Inspection Call by IC Number
   * @param {string} icNumber - IC Number
   * @returns {Promise<Object>} - API response with RM IC details
   */
  getRMInspectionCallByICNumber: async (icNumber) => {
    try {
      const response = await httpClient.get(
        `/raw-material/calls/ic-number/${encodeURIComponent(icNumber)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching RM inspection call:', error);
      throw error;
    }
  },

  /**
   * Get approved RM ICs with heat details for a specific PO
   * Used in Vendor Dashboard to display approved RM ICs
   * @param {string} poNo - Purchase Order Number
   * @returns {Promise<Object>} - API response with approved RM ICs and heat details
   */
  getApprovedRMICsWithHeatDetails: async (poNo) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/raw-material/approved-with-heats?po_no=${encodeURIComponent(poNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching approved RM ICs with heat details:', error);
      throw error;
    }
  },

  /**
   * Get heat numbers from a specific RM IC
   * @param {string} rmIcNumber - RM IC Number
   * @returns {Promise<Object>} - API response with heat numbers and details
   */
  getHeatNumbersFromRMIC: async (rmIcNumber) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/raw-material/${encodeURIComponent(rmIcNumber)}/heat-numbers`
      );
      return response;
    } catch (error) {
      console.error('Error fetching heat numbers from RM IC:', error);
      throw error;
    }
  },

  /**
   * Get completed RM IC certificate numbers for Process IC dropdown
   * Fetches from inspection_complete_details table filtered by ER prefix and optionally by PO number
   * @param {string} poNo - Purchase Order Number (optional)
   * @returns {Promise<Object>} - API response with list of completed RM IC certificate numbers
   */
  getCompletedRmIcNumbers: async (poSerialNo = null, callCategory = null) => {
    try {
      let endpoint = '/raw-material/completed-rm-ics';
      const params = [];
      if (poSerialNo) {
        params.push(`poSerialNo=${encodeURIComponent(poSerialNo)}`);
      }
      if (callCategory) {
        params.push(`callCategory=${encodeURIComponent(callCategory)}`);
      }
      if (params.length > 0) {
        endpoint += `?${params.join('&')}`;
      }
      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching completed RM IC certificate numbers:', error);
      throw error;
    }
  },

  /**
   * Get heat numbers by RM IC number
   * Fetches from rm_heat_quantities table based on the RM IC's ic_id
   * @param {string} rmIcNumber - RM IC Number (call_no from inspection_complete_details)
   * @returns {Promise<Object>} - API response with heat numbers and details
   */
  getHeatNumbersByRmIcNumber: async (rmIcNumber) => {
    try {
      const callNo = normalizeIc(rmIcNumber);
      const response = await httpClient.get(
        `/raw-material/heats-by-rm-ic/${encodeURIComponent(callNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching heat numbers by RM IC:', error);
      throw error;
    }
  },

  /**
   * Get chemical analysis by heat number
   * Fetches the most recent chemical analysis data for a given heat number from previous inspection calls
   * @param {string} heatNumber - Heat number to search for
   * @returns {Promise<Object>} - API response with chemical analysis data or null if not found
   */
  getChemicalAnalysisByHeatNumber: async (heatNumber) => {
    try {
      const response = await httpClient.get(
        `/raw-material/chemical-analysis/heat/${encodeURIComponent(heatNumber)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching chemical analysis by heat number:', error);
      throw error;
    }
  },

  // ============================================================
  // PROCESS INSPECTION CALLS
  // ============================================================

  /**
   * Create a new Process Inspection Call
   * @param {Object} processInspectionData - Process inspection call data
   * @returns {Promise<Object>} - API response with created IC details including auto-generated IC number
   */
  createProcessInspectionCall: async (processInspectionData) => {
    try {
      console.log('📤 Sending Process IC data to backend:', JSON.stringify(processInspectionData, null, 2));

      const response = await httpClient.post(
        '/process-material/inspectionCall',
        processInspectionData
      );

      console.log('✅ Backend response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error creating Process inspection call:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error status:', error.status);
      console.error('❌ Error data:', error.data);
      console.error('❌ Full error details:', JSON.stringify(error.data, null, 2));

      // Log the backend error message if available
      if (error.data && error.data.responseStatus) {
        console.error('❌ Backend error message:', error.data.responseStatus.message);
        console.error('❌ Backend error code:', error.data.responseStatus.errorCode);
        console.error('❌ Backend error type:', error.data.responseStatus.errorType);
      }

      throw error;
    }
  },

  /**
   * Get all Process Inspection Calls
   * @param {Object} filters - Optional filters (status, rm_ic_number, etc.)
   * @returns {Promise<Object>} - API response with list of Process ICs
   */
  getProcessInspectionCalls: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams
        ? `/inspection-calls/process?${queryParams}`
        : '/inspection-calls/process';

      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching Process inspection calls:', error);
      throw error;
    }
  },

  /**
   * Get approved Process Inspection Calls for a specific RM IC
   * Used in Final Inspection dropdown
   * @param {string} rmIcNumber - RM IC Number
   * @returns {Promise<Object>} - API response with approved Process ICs
   */
  getApprovedProcessInspectionCalls: async (rmIcNumber) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/process/approved?rm_ic_number=${encodeURIComponent(rmIcNumber)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching approved Process inspection calls:', error);
      throw error;
    }
  },

  /**
   * Get lot numbers from a specific Process IC
   * @param {string} processIcNumber - Process IC Number
   * @returns {Promise<Object>} - API response with lot numbers and details
   */
  getLotNumbersFromProcessIC: async (processIcNumber) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/process/${encodeURIComponent(processIcNumber)}/lot-numbers`
      );
      return response;
    } catch (error) {
      console.error('Error fetching lot numbers from Process IC:', error);
      throw error;
    }
  },

  // ============================================================
  // FINAL INSPECTION CALLS
  // ============================================================

  /**
   * Create a new Final Inspection Call
   * @param {Object} finalInspectionData - Final inspection call data with multiple lots
   * @returns {Promise<Object>} - API response with created IC details including auto-generated IC number
   */
  createFinalInspectionCall: async (finalInspectionData) => {
    try {
      console.log('📤 Sending Final IC data to backend:', JSON.stringify(finalInspectionData, null, 2));

      const response = await httpClient.post(
        '/final-material/inspectionCall',
        finalInspectionData
      );

      console.log('✅ Backend response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error creating Final inspection call:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error data:', error.data);
      console.error('❌ Full error response:', JSON.stringify(error, null, 2));
      if (error.data && error.data.responseStatus) {
        console.error('❌ Backend error details:', error.data.responseStatus);
      }
      throw error;
    }
  },

  /**
   * Get all Final Inspection Calls
   * @param {Object} filters - Optional filters (status, rm_ic_number, process_ic_number, etc.)
   * @returns {Promise<Object>} - API response with list of Final ICs
   */
  getFinalInspectionCalls: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams
        ? `/inspection-calls/final?${queryParams}`
        : '/inspection-calls/final';

      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching Final inspection calls:', error);
      throw error;
    }
  },

  /**
   * Get Final Inspection Call by IC Number
   * @param {string} icNumber - IC Number
   * @returns {Promise<Object>} - API response with Final IC details including all lots
   */
  getFinalInspectionCallByICNumber: async (icNumber) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/final/${encodeURIComponent(icNumber)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching Final inspection call:', error);
      throw error;
    }
  },

  /**
   * Get available lot numbers for Final Inspection
   * Based on selected RM IC and Process IC
   * @param {string} rmIcNumber - RM IC Number
   * @param {string} processIcNumber - Process IC Number
   * @returns {Promise<Object>} - API response with available lot numbers
   */
  getAvailableLotNumbersForFinal: async (rmIcNumber, processIcNumber) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/final/available-lots?rm_ic_number=${encodeURIComponent(rmIcNumber)}&process_ic_number=${encodeURIComponent(processIcNumber)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching available lot numbers for Final IC:', error);
      throw error;
    }
  },

  // ============================================================
  // COMMON METHODS
  // ============================================================

  /**
   * Get inspection call by ID (any type)
   * @param {number} icId - Inspection Call ID
   * @returns {Promise<Object>} - API response with IC details
   */
  getInspectionCallById: async (icId) => {
    try {
      const response = await httpClient.get(
        `/inspection-calls/${icId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching inspection call by ID:', error);
      throw error;
    }
  },

  /**
   * Update inspection call status
   * @param {string} icNumber - IC Number
   * @param {string} status - New status
   * @param {string} remarks - Optional remarks
   * @returns {Promise<Object>} - API response
   */
  updateInspectionCallStatus: async (icNumber, status, remarks = null) => {
    try {
      const response = await httpClient.patch(
        `/inspection-calls/${encodeURIComponent(icNumber)}/status`,
        { status, remarks }
      );
      return response;
    } catch (error) {
      console.error('Error updating inspection call status:', error);
      throw error;
    }
  },

  /**
   * Get all inspection calls for a vendor
   * @param {string} vendorId - Vendor ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} - API response with all ICs
   */
  getVendorInspectionCalls: async (vendorId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams({ ...filters, vendor_id: vendorId }).toString();
      const response = await httpClient.get(
        `/inspection-calls/vendor?${queryParams}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching vendor inspection calls:', error);
      throw error;
    }
  },

  /**
   * Get vendor inspection calls with workflow status
   * @param {string} vendorId - Vendor ID
   * @returns {Promise<Object>} - API response with inspection calls and workflow status
   */
  getVendorInspectionCallsWithStatus: async (vendorId) => {
    try {
      const response = await httpClient.get(
        `/vendor/inspection-calls/status?vendorId=${vendorId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching vendor inspection calls with status:', error);
      throw error;
    }
  },

  // ==================== FINAL INSPECTION CALL DROPDOWN APIs ====================

  /**
   * Get Process IC certificate numbers for Final Inspection Call
   * Fetches from inspection_complete_details where CALL_NO starts with 'EP'
   * Filtered by vendor_id
   * @param {string} vendorId - Vendor ID
   * @returns {Promise<Object>} - API response with list of certificate numbers
   */
  getProcessIcCertificates: async (vendorId) => {
    try {
      const response = await httpClient.get(
        `/final-material/process-ic-certificates?vendorId=${vendorId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching Process IC certificates:', error);
      throw error;
    }
  },

  /**
   * Get RM IC numbers by Process IC certificate number
   * @param {string} certificateNo - Process IC certificate number
   * @returns {Promise<Object>} - API response with list of RM IC numbers
   */
  getRmIcNumbersByCertificate: async (certificateNo) => {
    try {
      const response = await httpClient.get(
        `/final-material/rm-ic-numbers?certificateNo=${encodeURIComponent(certificateNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching RM IC numbers:', error);
      throw error;
    }
  },

  /**
   * Get Lot numbers by RM IC number
   * @param {string} rmIcNumber - RM IC number
   * @returns {Promise<Object>} - API response with list of lot numbers
   */
  getLotNumbersByRmIc: async (rmIcNumber) => {
    try {
      const response = await httpClient.get(
        `/final-material/lot-numbers?rmIcNumber=${encodeURIComponent(rmIcNumber)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching lot numbers:', error);
      throw error;
    }
  },

  // ==================== NEW METHODS FOR REVERSED DROPDOWN FLOW ====================

  /**
   * Get RM IC certificate numbers for Final Inspection Call (NEW FLOW)
   * Fetches from inspection_complete_details where CALL_NO starts with 'ER'
   * Filtered by PO Serial Number
   * @param {string} poSerialNo - PO Serial Number
   * @returns {Promise<Object>} - API response with list of RM IC certificate numbers
   */
  getRmIcCertificates: async (poSerialNo, callCategory = null) => {
    try {
      let endpoint = `/final-material/rm-ic-certificates?poSerialNo=${encodeURIComponent(poSerialNo)}`;
      if (callCategory) {
        endpoint += `&callCategory=${encodeURIComponent(callCategory)}`;
      }
      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching RM IC certificates:', error);
      throw error;
    }
  },

  /**
   * Get Diverted Process IC certificate numbers for target PO Serial Number
   * @param {string} poSerialNo - PO Serial Number
   * @returns {Promise<Object>} - API response with list of Process IC certificate numbers
   */
  getDivertedProcessIcs: async (poSerialNo) => {
    try {
      const endpoint = `/final-material/diverted-process-ics?poSerialNo=${encodeURIComponent(poSerialNo)}`;
      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching diverted Process IC certificates:', error);
      throw error;
    }
  },

  /**
   * Get RM IC certificate numbers associated with a Process IC
   * @param {string} processCertificateNo - Process IC certificate number
   * @returns {Promise<Object>} - API response with list of RM IC numbers
   */
  getRmIcsByProcessCertificate: async (processCertificateNo) => {
    try {
      const endpoint = `/final-material/rm-ic-by-process?processCertificateNo=${encodeURIComponent(processCertificateNo)}`;
      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching RM ICs by Process certificate:', error);
      throw error;
    }
  },

  /**
   * Get Process IC certificate numbers by RM IC certificate (NEW FLOW)
   * @param {string} rmCertificateNo - RM IC certificate number
   * @returns {Promise<Object>} - API response with list of Process IC certificate numbers
   */
  getProcessIcCertificatesByRmCertificate: async (rmCertificateNo) => {
    try {
      const response = await httpClient.get(
        `/final-material/process-ic-by-rm?rmCertificateNo=${encodeURIComponent(rmCertificateNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching Process IC certificates by RM certificate:', error);
      throw error;
    }
  },

  /**
   * Get Lot numbers by both RM IC and Process IC certificates (NEW FLOW)
   * @param {string} rmCertificateNo - RM IC certificate number
   * @param {string} processCertificateNo - Process IC certificate number
   * @returns {Promise<Object>} - API response with list of lot numbers
   */
  getLotNumbersByCertificates: async (rmCertificateNo, processCertificateNo) => {
    try {
      const response = await httpClient.get(
        `/final-material/lot-numbers-by-certificates?rmCertificateNo=${encodeURIComponent(rmCertificateNo)}&processCertificateNo=${encodeURIComponent(processCertificateNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching lot numbers by certificates:', error);
      throw error;
    }
  },

  /**
   * Get Heat numbers by lot number and RM IC certificate (NEW FLOW)
   * @param {string} lotNumber - Lot number
   * @param {string} rmCertificateNo - RM IC certificate number
   * @returns {Promise<Object>} - API response with list of heat numbers
   */
  getHeatNumbersByLotNumber: async (lotNumber, rmCertificateNo, processCertificateNo) => {
    try {
      let url = `/final-material/heat-numbers-by-lot?lotNumber=${encodeURIComponent(lotNumber || '')}&rmCertificateNo=${encodeURIComponent(rmCertificateNo || '')}`;
      if (processCertificateNo) {
        url += `&processCertificateNo=${encodeURIComponent(processCertificateNo)}`;
      }
      const response = await httpClient.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching heat numbers by lot number:', error);
      throw error;
    }
  },

  /**
   * Get Process IC certificate numbers for multiple RM IC certificates (MULTI-SELECT)
   * @param {Array<string>} rmCertificateNos - Array of RM IC certificate numbers
   * @returns {Promise<Object>} - API response with list of Process IC certificate numbers
   */
  getProcessIcCertificatesByMultipleRmCertificates: async (rmCertificateNos) => {
    try {
      const params = rmCertificateNos.map(cert => `rmCertificateNos=${encodeURIComponent(cert)}`).join('&');
      const response = await httpClient.get(
        `/final-material/process-ic-by-multiple-rm?${params}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching Process IC certificates by multiple RM certificates:', error);
      throw error;
    }
  },

  /**
   * Get Lot numbers for multiple RM IC and Process IC certificates (MULTI-SELECT)
   * @param {Array<string>} rmCertificateNos - Array of RM IC certificate numbers
   * @param {Array<string>} processCertificateNos - Array of Process IC certificate numbers
   * @returns {Promise<Object>} - API response with list of lot numbers
   */
  getLotNumbersByMultipleCertificates: async (rmCertificateNos, processCertificateNos) => {
    try {
      const rmParams = rmCertificateNos.map(cert => `rmCertificateNos=${encodeURIComponent(cert)}`).join('&');
      const processParams = processCertificateNos.map(cert => `processCertificateNos=${encodeURIComponent(cert)}`).join('&');
      const response = await httpClient.get(
        `/final-material/lot-numbers-by-multiple-certificates?${rmParams}&${processParams}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching lot numbers by multiple certificates:', error);
      throw error;
    }
  },

  // ==================== HEAT SUMMARY APIs ====================

  /**
   * Get PO Number by RM IC Call ID
   * Extracts the middle part of RM IC number (e.g., "N/ER-01150001/RAJK" → "ER-01150001")
   * @param {string} rmIcNumber - Full RM IC number
   * @returns {Promise<Object>} - API response with PO number
   */
  getPoNumberByRmIc: async (rmIcNumber) => {
    try {
      // Extract call number from RM IC number
      // Format: "ANYPART/ER-01150001/ANYPART" → "ER-01150001"
      const callNoMatch = rmIcNumber.match(/[^/]+\/([^/]+)\//);
      const callNo = callNoMatch ? callNoMatch[1] : rmIcNumber;

      console.log(`📋 Fetching PO number for RM IC: ${rmIcNumber}, Call No: ${callNo}`);

      const response = await httpClient.get(
        `/processIe/getPoNumnerByCallId/${callNo}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching PO number by RM IC:', error);
      throw error;
    }
  },

  /**
   * Get Heat Summary Data for a specific Heat Number and PO Number
   * Returns manufactured quantity, accepted quantity, and other heat details
   * @param {string} heatNo - Heat number
   * @param {string} poNo - PO number
   * @returns {Promise<Object>} - API response with heat summary data
   */
  getHeatSummaryData: async (heatNo, poNo, callNo = '', vendorCode = '') => {
    try {
      console.log(`📊 Fetching heat summary for Heat: ${heatNo}, PO: ${poNo}, CallNo: ${callNo}, Vendor: ${vendorCode}`);

      const params = new URLSearchParams();
      if (callNo) params.append('callNo', callNo);
      if (vendorCode) params.append('vendorCode', vendorCode);

      const queryString = params.toString();
      const url = queryString 
        ? `/processIe/getManufaturedQtyOfPo/${encodeURIComponent(heatNo)}/${encodeURIComponent(poNo)}?${queryString}`
        : `/processIe/getManufaturedQtyOfPo/${encodeURIComponent(heatNo)}/${encodeURIComponent(poNo)}`;

      const response = await httpClient.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching heat summary data:', error);
      throw error;
    }
  },

  /**
   * Get previously offered quantities for a specific heat number and PO series
   * Returns the total quantity offered in previous Process Inspection Calls
   * @param {string} heatNo - Heat number
   * @param {string} poSerialNo - PO Serial Number
   * @returns {Promise<Object>} - API response with previously offered quantity
   */
  getPreviouslyOfferedQuantity: async (heatNo, poSerialNo) => {
    try {
      console.log(`📊 Fetching previously offered quantity for Heat: ${heatNo}, PO Serial: ${poSerialNo}`);

      const response = await httpClient.get(
        `/process-material/offered-qty/${encodeURIComponent(heatNo)}/${encodeURIComponent(poSerialNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching previously offered quantity:', error);
      throw error;
    }
  },

  /**
   * Get RM IC details by Certificate Number
   * First fetches the IC number from inspection_complete_details using certificate number
   * Then fetches the inspection call details using the IC number
   * @param {string} certificateNo - RM IC certificate number (e.g., "N/ER-0118005/RAJK")
   * @returns {Promise<Object>} - API response with RM IC details including company_id, unit_id
   */
  getRmIcDetailsByCertificateNo: async (certificateNo) => {
    try {
      console.log(`📋 Fetching RM IC details for Certificate Number: ${certificateNo}`);

      // First, get the IC number from the certificate number
      // The certificate number is stored in inspection_complete_details.CERTIFICATE_NO
      // and the IC number is in inspection_complete_details.CALL_NO
      const response = await httpClient.get(
        `/raw-material/ic-by-certificate/${encodeURIComponent(certificateNo)}`
      );

      console.log(`✅ Successfully fetched RM IC details:`, response);
      return response;
    } catch (error) {
      console.error('❌ Error fetching RM IC details by certificate:', error);
      console.error('   Error message:', error.message);
      console.error('   Error status:', error.status);
      throw error;
    }
  },

  /**
   * Get RM IC details by IC Number (to fetch company_id, unit_id, etc.)
   * Uses the /raw-material/calls/ic-number endpoint which returns InspectionCallDto
   * with companyId, unitId, companyName, unitName, unitAddress fields
   * @param {string} icNumber - RM IC number
   * @returns {Promise<Object>} - API response with RM IC details
   */
  getRmIcDetailsByIcNumber: async (icNumber) => {
    try {
      console.log(`📋 Fetching RM IC details for IC Number: ${icNumber}`);
      const response = await httpClient.get(
        `/raw-material/calls/ic-number/${encodeURIComponent(icNumber)}`
      );
      console.log(`✅ Successfully fetched RM IC details:`, response);
      return response;
    } catch (error) {
      console.error('❌ Error fetching RM IC details:', error);
      console.error('   Error message:', error.message);
      console.error('   Error status:', error.status);
      throw error;
    }
  },

  /**
   * Get accepted quantity for a specific lot and request ID
   * @param {string} requestId - Process IC Request ID (filtered middle part)
   * @param {string} lotNumber - Lot Number
   * @param {string} heatNo - Heat Number
   * @returns {Promise<Object>} - API response with accepted quantity
   */
  getAcceptedQtyForLot: async (requestId, lotNumber, heatNo) => {
    try {
      const response = await httpClient.get(
        `/processIe/accepted-qty-for-lot?requestId=${encodeURIComponent(requestId)}&lotNumber=${encodeURIComponent(lotNumber)}&heatNo=${encodeURIComponent(heatNo)}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching accepted quantity for lot:', error);
      throw error;
    }
  },

  /**
   * Get the total quantity offered in previous Final Inspection Calls for a
   * specific heat and lot
   * @param {string} heatNo - Heat number
   * @param {string} lotNo - Lot number
   * @param {string} [poNo] - Purchase Order Number
   * @param {string} [poSerialNo] - PO Serial Number
   * @returns {Promise<Object>} - API response with offered quantity
   */
  getOfferedEarlierQuantity: async (heatNo, lotNo, poNo, poSerialNo) => {
    try {
      let url = `/final-material/offered-earlier?heatNo=${encodeURIComponent(heatNo)}&lotNo=${encodeURIComponent(lotNo)}`;
      if (poNo) {
        url += `&poNo=${encodeURIComponent(poNo)}`;
      }
      if (poSerialNo) {
        url += `&poSerialNo=${encodeURIComponent(poSerialNo)}`;
      }
      const response = await httpClient.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching offered earlier quantity:', error);
      throw error;
    }
  },

  /**
   * Modify a Raw Material Inspection Call (PATCH)
   * Endpoint: PATCH /api/raw-material/modifyInspectionCall?icNumber=...
   * Only non-null fields in the DTO will be updated (backend uses null-check to skip unchanged fields).
   *
   * @param {string} icNumber - IC Number to modify
   * @param {Object} icFields - Partial InspectionCallRequestDto fields to update
   * @param {Object} rmFields - Partial RmInspectionDetailsRequestDto fields to update
   * @returns {Promise<Object>} - API response with updated InspectionCall
   */
  modifyRMInspectionCall: async (icNumber, icFields = {}, rmFields = {}) => {
    try {
      console.log('📝 Modifying RM Inspection Call:', icNumber, { icFields, rmFields });
      const payload = {
        inspectionCall: Object.keys(icFields).length > 0 ? icFields : null,
        rmInspectionDetails: Object.keys(rmFields).length > 0 ? rmFields : null
      };
      const response = await httpClient.patch(
        `/raw-material/modifyInspectionCall?icNumber=${encodeURIComponent(icNumber)}`,
        payload
      );
      console.log('✅ Modify RM IC response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error modifying RM inspection call:', error);
      throw error;
    }
  },

  /**
   * Modify a Process Inspection Call (PATCH)
   * Endpoint: PATCH /api/process-material/modifyInspectionCall?icNumber=...
   *
   * @param {string} icNumber - IC Number to modify
   * @param {Object} icFields - Partial InspectionCallRequestDto fields to update
   * @param {Array} processDetails - Partial ProcessInspectionDetailsRequestDto fields to update
   * @returns {Promise<Object>} - API response
   */
  modifyProcessInspectionCall: async (icNumber, icFields = {}, processDetails = []) => {
    try {
      console.log('📝 Modifying Process Inspection Call:', icNumber, { icFields, processDetails });
      const payload = {
        inspectionCall: Object.keys(icFields).length > 0 ? icFields : null,
        processInspectionDetails: processDetails.length > 0 ? processDetails : null
      };
      const response = await httpClient.patch(
        `/process-material/modifyInspectionCall?icNumber=${encodeURIComponent(icNumber)}`,
        payload
      );
      console.log('✅ Modify Process IC response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error modifying Process inspection call:', error);
      throw error;
    }
  },

  /**
   * Modify a Final Inspection Call (PATCH)
   * Endpoint: PATCH /api/final-material/modifyInspectionCall?icNumber=...
   *
   * @param {string} icNumber - IC Number to modify
   * @param {Object} icFields - Partial InspectionCallRequestDto fields to update
   * @param {Object} finalDetails - Partial FinalInspectionDetailsRequestDto fields to update
   * @param {Array} lotDetails - Partial FinalInspectionLotDetailsRequestDto fields to update
   * @returns {Promise<Object>} - API response
   */
  modifyFinalInspectionCall: async (icNumber, icFields = {}, finalDetails = {}, lotDetails = []) => {
    try {
      console.log('📝 Modifying Final Inspection Call:', icNumber, { icFields, finalDetails, lotDetails });
      const payload = {
        inspectionCall: Object.keys(icFields).length > 0 ? icFields : null,
        finalInspectionDetails: Object.keys(finalDetails).length > 0 ? finalDetails : null,
        finalLotDetails: lotDetails.length > 0 ? lotDetails : null
      };
      const response = await httpClient.patch(
        `/final-material/modifyInspectionCall?icNumber=${encodeURIComponent(icNumber)}`,
        payload
      );
      console.log('✅ Modify Final IC response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error modifying Final inspection call:', error);
      throw error;
    }
  },

  /**
   * Get RM Inspection Call full details by IC Number (for modify pre-fill)
   * Uses GET /api/raw-material/calls/ic-number/{icNumber}
   * @param {string} icNumber - IC Number
   * @returns {Promise<Object>} - API response
   */
  getICDetailsByNumber: async (icNumber) => {
    try {
      console.log(`📋 Fetching IC details for pre-fill: ${icNumber}`);
      const response = await httpClient.get(
        `/raw-material/calls/ic-number/${encodeURIComponent(icNumber)}`
      );
      return response;
    } catch (error) {
      console.error('❌ Error fetching IC details by number:', error);
      throw error;
    }
  },

  /**
   * Withdraw an inspection call
   * @param {Object} withdrawalData - Data for withdrawal (workflowTransitionId, requestId, remarks, actionBy)
   * @returns {Promise<Object>} - API response
   */
  withdrawCall: async (withdrawalData) => {
    try {
      console.log('📤 Sending Withdrawal request:', withdrawalData);
      const response = await httpClient.post(
        '/workflow/withdraw',
        withdrawalData
      );
      console.log('✅ Withdrawal successful:', response);
      return response;
    } catch (error) {
      console.error('❌ Error withdrawing call:', error);
      throw error;
    }
  },

  /**
   * Get workflow transition history for a specific request ID
   * @param {string} requestId - Request ID (e.g. call number or production declaration ID)
   * @returns {Promise<Object>} - API response with workflow history
   */
  getWorkflowHistory: async (requestId) => {
    try {
      console.log(`📋 Fetching workflow transition history for Request ID: ${requestId}`);
      const response = await httpClient.get(
        `/railpad-workflow/WorkflowTransitionHistory?requestId=${encodeURIComponent(requestId)}`
      );
      return response;
    } catch (error) {
      console.error('❌ Error fetching workflow transition history:', error);
      throw error;
    }
  },

  /**
   * Get Process Inspection Call full details by IC Number (for modify pre-fill)
   * GET /api/process-material/inspection-call-details/{callNo}
   * @param {string} icNumber - IC Number
   * @returns {Promise<Object>} - API response
   */
  getProcessICDetailsByNumber: async (icNumber) => {
    try {
      console.log(`📋 Fetching Process IC details for pre-fill: ${icNumber}`);
      const response = await httpClient.get(
        `/process-material/inspection-call-details/${encodeURIComponent(icNumber)}`
      );
      return response;
    } catch (error) {
      console.error('❌ Error fetching Process IC details by number:', error);
      throw error;
    }
  },

  /**
   * Get Final Inspection Call full details by IC Number (for modify pre-fill)
   * GET /api/final-material/inspection/{callNo}
   * @param {string} icNumber - IC Number
   * @returns {Promise<Object>} - API response
   */
  getFinalICDetailsByNumber: async (icNumber) => {
    try {
      console.log(`📋 Fetching Final IC details for pre-fill: ${icNumber}`);
      const response = await httpClient.get(
        `/final-material/inspection/${encodeURIComponent(icNumber)}`
      );
      return response;
    } catch (error) {
      console.error('❌ Error fetching Final IC details by number:', error);
      throw error;
    }
  },

  /**
   * Get Heat Details by IC Number (for Vendor Dashboard Completed Calls Action modal)
   * GET /api/rm-inspection/heat-details/{callNo}
   * @param {string} icNumber - IC Number
   * @returns {Promise<Object>} - API response
   */
  getHeatDetails: async (icNumber) => {
    try {
      console.log(`📋 Fetching Heat Details for IC: ${icNumber}`);
      const response = await httpClient.get(
        `/rm-inspection/heat-details/${encodeURIComponent(icNumber)}`
      );
      return response;
    } catch (error) {
      console.error('❌ Error fetching Heat Details:', error);
      throw error;
    }
  },

  /**
   * Fetch cancelled calls requiring charges payment for ERC vendor
   */
  getCancelledCallsForPayment: async (plantId, vendorCode) => {
    try {
      const params = {};
      if (plantId) params.plantId = plantId;
      if (vendorCode) params.vendorCode = vendorCode;
      const response = await httpClient.get('/cancelledCallsForPayment', { params });
      return response?.responseData || response?.data || response || [];
    } catch (error) {
      console.error('Error fetching cancelled calls for payment:', error);
      return [];
    }
  },

  /**
   * Check if plant is blocked from raising new calls due to unpaid cancellation charges
   */
  checkPlantPaymentBlock: async (plantId, vendorCode) => {
    try {
      const params = {};
      if (plantId) params.plantId = plantId;
      if (vendorCode) params.vendorCode = vendorCode;
      const response = await httpClient.get('/checkPlantPaymentBlock', { params });
      return response?.responseData || response?.data || { blocked: false };
    } catch (error) {
      console.error('Error checking plant payment block:', error);
      return { blocked: false };
    }
  },

  /**
   * Get detailed cancellation records for a call
   */
  getCancellationDetails: async (callNo) => {
    try {
      const response = await httpClient.get(`/cancellationDetails/${encodeURIComponent(callNo)}`);
      return response?.responseData || response?.data || null;
    } catch (error) {
      console.error('Error fetching cancellation details:', error);
      return null;
    }
  },

  /**
   * Verify IBS Payment status via backend proxy
   */
  verifyIbsPayment: async (caseNo, callDate, ibsCallSno, callNo) => {
    try {
      const response = await httpClient.post('/verify-ibs-payment', {
        caseNo,
        callDate,
        ibsCallSno: Number(ibsCallSno),
        callNo
      });
      return response?.responseData || response?.data || response;
    } catch (error) {
      console.error('Error in verifyIbsPayment:', error);
      throw error;
    }
  },

  /**
   * Mark cancelled call payment as approved in backend liability table
   */
  markPaymentApproved: async (callNo) => {
    try {
      const response = await httpClient.post('/mark-payment-approved', { callNo });
      return response?.responseData || response?.data || response;
    } catch (error) {
      console.error('Error in markPaymentApproved:', error);
      throw error;
    }
  }
};

export default inspectionCallService;

